/**
 * How large a signature logo may be shown, and what a corner drag does to it (#1452).
 *
 * The size lives in the image's `width`/`height` attributes rather than in CSS, because that is
 * what mail clients honour. `signature-editor-policy.ts` keeps those attributes only while they
 * are integers no greater than 1200 and drops anything else on the way in — a size outside that
 * range would not survive a save, and the old one would silently come back. So the ceiling here is
 * the policy's ceiling and every result is a whole number.
 *
 * Nothing here touches the uploaded file. The logo travels as a `cid:` attachment; a resize
 * changes two numbers and leaves the bytes alone.
 *
 * Deliberately free of React, Lexical and the DOM, so the arithmetic the handles and the numeric
 * field share can be read and tested in one place.
 */

/** The largest width or height `signature-editor-policy.ts` will keep. */
export const SIGNATURE_LOGO_MAX_PX = 1200;

/** Small enough for a wordmark beside an address, large enough that a handle stays grabbable. */
export const SIGNATURE_LOGO_MIN_PX = 32;

export const SIGNATURE_LOGO_CORNERS = ["nw", "ne", "sw", "se"] as const;

export type SignatureLogoCorner = (typeof SIGNATURE_LOGO_CORNERS)[number];

export interface SignatureLogoSize {
  readonly width: number;
  readonly height: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * The widths this image can be given, both ends inclusive.
 *
 * The upper bound is whichever of the two dimensions reaches the policy ceiling first: a wide
 * banner runs out of width, a tall crest runs out of height. The lower bound yields to the upper
 * one, so an image whose shape cannot reach the minimum is still resizable rather than stuck.
 */
export function signatureLogoWidthBounds(start: SignatureLogoSize): { min: number; max: number } {
  const ratio = start.height / start.width;
  const max = Number.isFinite(ratio) && ratio > 0
    ? Math.max(1, Math.min(SIGNATURE_LOGO_MAX_PX, Math.floor(SIGNATURE_LOGO_MAX_PX / ratio)))
    : SIGNATURE_LOGO_MAX_PX;
  return { min: Math.min(SIGNATURE_LOGO_MIN_PX, max), max };
}

/** The image at the given width, with its shape kept and both dimensions within the policy. */
export function signatureLogoSizeAt(start: SignatureLogoSize, width: number): SignatureLogoSize {
  const bounds = signatureLogoWidthBounds(start);
  const next = clamp(Math.round(width) || bounds.min, bounds.min, bounds.max);
  return { width: next, height: Math.max(1, Math.round((next * start.height) / start.width)) };
}

/**
 * The image after a corner has been dragged by `deltaX` horizontally.
 *
 * Only the horizontal movement counts. A corner drag is rarely straight, and letting the vertical
 * component speak too would have it argue with the locked aspect ratio — one pixel up and one
 * pixel right would mean two different sizes at once.
 */
export function signatureLogoResize(
  start: SignatureLogoSize,
  deltaX: number,
  corner: SignatureLogoCorner,
): SignatureLogoSize {
  const outwards = corner === "ne" || corner === "se" ? 1 : -1;
  return signatureLogoSizeAt(start, start.width + deltaX * outwards);
}

/**
 * The size a drag starts from: the attributes the image already carries.
 *
 * They are the size the signature is saved with, so they are the size the person is adjusting.
 * An image that carries none — hand-written source HTML — falls back to what the browser loaded,
 * and to 1:1 when even that is unknown, which keeps the handles working instead of dividing by a
 * zero width.
 */
export function signatureLogoStartSize(
  attrs: { readonly width?: string | null; readonly height?: string | null },
  natural: SignatureLogoSize,
): SignatureLogoSize {
  const width = Number(attrs.width);
  const height = Number(attrs.height);
  if (Number.isInteger(width) && Number.isInteger(height) && width > 0 && height > 0) {
    return { width, height };
  }
  return {
    width: Math.max(1, Math.round(natural.width) || 0),
    height: Math.max(1, Math.round(natural.height) || 0),
  };
}
